/* eslint-disable max-len */
import {
  getCarImages, INTERIOR_COLOR, INTERIOR_TYPE, mapCarCategory, PARTS_SHOP_BRAND_MAPPING, VEHICLE_EQUIPMENT,
} from '@plugins/vehiclefleetapp';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AppBar,
  Box,
  Button,
  Divider,
  Grid2,
  IconButton,
  LinearProgress,
  Link as MuiLink,
  Paper,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import {
  FiCalendar, FiChevronDown, FiChevronLeft, FiMail, FiPhone,
} from 'react-icons/fi';
import { MediaElement } from '@plugins/next-cms-core';
import { gql } from '@apollo/client';
import useConsent from '@lib/hooks/useConsent';
import useConsentManager from '@lib/hooks/useConsentManager';
import { useEffect, useState } from 'react';
import { BRAND_LOGO_MAP, CarListItemCardContent } from '@components/vehiclefleet/InventoryCarListItem';
import Container from '@components/atoms/Container';
import { DateTime } from 'luxon';
import DefaultTemplate from '@components/templates/DefaultTemplate';
import Head from 'next/head';
import Image from '@components/atoms/Image';
import Link from '@components/atoms/Link';
import ModelRequestForm from '@forms/ModelRequestForm';
import OnPageJumpTarget from '@components/atoms/OnPageJumpTarget';
import Script from 'next/script';
import PageInsetTop from '@components/atoms/PageInsetTop';
import PollutionBadge from '@components/vehiclefleet/PollutionBadge';
import getConfig from 'next/config';
import { transformBrandTitle } from '@components/vehiclefleet/CarBrandTitle';
import { useRouter } from 'next/router';
import Hidden from '@components/atoms/Hidden';
import get from 'lodash/get';
import uniq from 'lodash/uniq';
import StaffMember from '@components/molecules/StaffMember';
import { trackEvent } from '@components/organisms/Tracking';
import { useTranslation } from 'next-i18next';
import ModelName from '@components/vehiclefleet/ModelName';
import ConsumptionEmissionsNotice, { GRAPHQL_REQUIRED_PROPS_FOR_CONSUMPTION, isEmissionsDisplayRequired } from '@components/vehiclefleet/ConsumptionEmissionsNotice';
import { getAppData } from '@lib/appInitialState';
import ImageGallery from '@components/atoms/Swiper';
import InventoryCarsBlock from '@components/contentTypes/InventoryCarsBlock';
import ShareButton from '@components/atoms/ShareButton';
import ConsentRequiredFallback from '@components/molecules/ConsentRequiredFallback';
import dynamic from 'next/dynamic';

const { publicRuntimeConfig } = getConfig();

const EmbedElementDynamic = dynamic(() => import('@plugins/next-cms-core/components/elements/EmbedElement'), { ssr: false });

export default function CarDetails({
  item,
  contacts,
  carSearchConfig,
}) {
  const theme = useTheme();
  const router = useRouter();
  const {
    t,
    i18n,
  } = useTranslation();
  const [activeFirstTab, setFirstTab] = useState('carDetails');
  const [activeSecondTab, setSecondTab] = useState('request');
  const hasSantanderConsent = useConsent('santander');
  const { changeConsent } = useConsentManager();

  useEffect(() => {
    let interval = null;

    if (window.location.hash === '#show-financing-calculator') {
      interval = setInterval(() => {
        if (document.querySelector('#san-float-widget-btn')) {
          clearInterval(interval);
          window.location.hash = '';
          handleShowFinancingForm();
        }
      }, 250);
    }

    if (window.location.hash === '#show-contact-form') {
      window.location.hash = '';
      setTimeout(() => {
        handleShowRequestForm();
      }, 250);
    }

    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!item) {
    router.push(NOT_AVAILABLE_URL[i18n.language]);
    return null;
  }

  const modelPageUrl = (carSearchConfig.urlMappings ?? [])
    .find((m) => m.model === item.attributes.baselineModel)?.url;
  const { isPurchasingEnabled } = carSearchConfig;
  const carCategory = mapCarCategory(item.attributes.category);
  const detailUrl = `${publicRuntimeConfig.PUBLIC_URL}/fahrzeugsuche/${item.attributes.slug}`;
  const imageThumbnails = getCarImages(item);
  const images = getCarImages(item, true);
  const partsShopMapping = PARTS_SHOP_BRAND_MAPPING[item.attributes.brand];
  let carState = 'usedVehicle';

  if (!item.attributes.initialRegistrationDate) {
    carState = 'newVehicle';
  } else if (item.attributes.isDemonstrationVehicle) {
    carState = 'demonstrationCar';
  } else if (item.attributes.isDailyAdmission) {
    carState = 'dailyAdmission';
  }

  const pageTitle = `${item.attributes.brand} ${item.attributes.baselineModel} ${item.attributes.vehicleId} [${i18n.language}]`;

  if (item.attributes.isDeleted) {
    router.push(`${NOT_AVAILABLE_URL[i18n.language]}#id=${item.id}&brand=${item.attributes.brand}&model=${item.attributes.baselineModel}`);
    return null;
  }

  /* useEffect(() => {
    if(activeTab !== 'twinner') {
      return () => {};
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://data.twinner.com/displaydata/widget/api-viewer-360.js';
    script.onload = () => {
      const twinner = new window.Twinner360Viewer({
        element: document.getElementById('widget-container'),
        language: 'de',
        theme: '[theme]',
        url: 'https://data.twinner.com/displaydata/20221103/102/135704_1192379f-ef40-4740-b903-d0b7a6640eee/',
        annotation: {
          isVisible: 'true'
        },
        viewModes: {
          initial: '8',
          disabled: '[3]'
        }
      });
    };

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [activeTab]); */

  const handleCheckout = () => {
    // @TODO
    router.push(`/fahrzeugsuche/checkout/${item.attributes.internalId}`);
  };
  const handleShowFinancingForm = () => {
    const element = document.querySelector('#san-float-widget-btn');

    if (!element) {
      if (!hasSantanderConsent) {
        changeConsent();
        return;
      }

      console.warn('Santander widget not available');
      return;
    }

    const event = document.createEvent('Event');
    event.initEvent('click', true, true);
    element.dispatchEvent(event);

    trackEvent('Contact');
    trackEvent('ContactCustom', {
      channel: 'Financing',
      origin: 'CarDetailPage',
    });
  };

  const handleShowRequestForm = (e, isRequestedTestDrive) => {
    setSecondTab('request');
    OnPageJumpTarget.scrollToElement('breakpoint-tabs');
    if (isRequestedTestDrive) {
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('requestTestDrive'));
      }, 1000);
    }
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        url: window.location.href,
        title: pageTitle,
      });
    } catch (err) {
      await navigator.clipboard.writeText(window.location.href);
      console.error('Copy to clipboard failed:', err.message);
    }
  };

  return (
    <>
      <Head>
        <title>
          {pageTitle}
        </title>
        <meta
          content={t('pages.vehiclefleetapp.slug.metaDescription', {
            brand: item.attributes.brand,
            model: item.attributes.model,
            vehicleId: item.attributes.vehicleId,
            vehicleCategory: carCategory.vehicleCategory,
            carState: t(`pages.vehiclefleetapp.slug.vehicleTypes.${carState}`),
            branch: getLocalizedBranch(item, i18n).attributes.name,
          })}
          name="description"
        />
        <meta key="og:image" content={images[0]} property="og:image" />
        <meta content={pageTitle} property="og:title" />
      </Head>
      <DefaultTemplate
        leasingProduct={item.attributes.leasingProduct}
        warrantyNotice={item.attributes.brand === 'MITSUBISHI'}
      >
        <PageInsetTop />
        <Container>
          <Box mb={2} mt={2}>
            <CarAdHeader t={t} />
          </Box>
          {/* <Box mb={5}>
            <Banner />
          </Box> */}
          <Box mb={5}>
            <Grid2 container spacing={5}>
              <Grid2 size={{
                md: 8,
                xs: 12,
              }}
              >
                <Hidden dir="up" size="md">
                  <CardAdSummaryHeader
                    handleShare={handleShare}
                    item={item}
                    modelPageUrl={modelPageUrl}
                    theme={theme}
                  />
                </Hidden>
                <ImageGallery
                  images={images}
                  imageThumbnails={imageThumbnails}
                />
              </Grid2>
              <Grid2 size={{
                md: 4,
                xs: 12,
              }}
              >
                <CarAdSummary
                  carCategory={carCategory}
                  contacts={contacts}
                  detailUrl={detailUrl}
                  handleShare={handleShare}
                  i18n={i18n}
                  isPurchasingEnabled={isPurchasingEnabled}
                  item={item}
                  modelPageUrl={modelPageUrl}
                  onCheckout={handleCheckout}
                  onShowFinancingForm={handleShowFinancingForm}
                  onShowRequestForm={handleShowRequestForm}
                  t={t}
                />
              </Grid2>
            </Grid2>
          </Box>
          <OnPageJumpTarget name="new-tabs" />
          <Hidden dir="up" size="md">
            <Box
              sx={{
                mb: 2,
                '& .MuiAccordionSummary-root': {
                  bgcolor: 'quaternary.main',
                },
              }}
            >
              <Accordion defaultExpanded>
                <AccordionSummary
                  expandIcon={<FiChevronDown color={theme.palette.primary.main} />}
                  sx={{
                    color: theme.palette.primary.main,
                  }}
                >
                  <Typography>
                    {t('pages.vehiclefleetapp.slug.tabs.carDetails')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: theme.palette.primary.main,
                    borderBottomLeftRadius: theme.shape.borderRadius,
                    borderBottomRightRadius: theme.shape.borderRadius,
                  }}
                >
                  <CarDetailsTab
                    i18n={i18n}
                    isAccordion
                    item={item}
                    t={t}
                    theme={theme}
                  />
                </AccordionDetails>
              </Accordion>
              <Accordion>
                <AccordionSummary
                  expandIcon={<FiChevronDown color={theme.palette.primary.main} />}
                  sx={{
                    color: theme.palette.primary.main,
                  }}
                >
                  <Typography>
                    {t('pages.vehiclefleetapp.slug.tabs.furnishing')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <CarAdDetails item={item} t={t} />
                </AccordionDetails>
              </Accordion>
              {isEmissionsDisplayRequired(item) && (
                <Accordion>
                  <AccordionSummary
                    expandIcon={<FiChevronDown color={theme.palette.primary.main} />}
                    sx={{
                      color: theme.palette.primary.main,
                    }}
                  >
                    <Typography>
                      {t('pages.vehiclefleetapp.slug.tabs.emissionData')}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box>
                      <ConsumptionEmissionsNotice
                        car={item}
                        variant="extended"
                      />
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          </Hidden>
          <Hidden dir="down" size="md">
            <Paper
              elevation={DEFAULT_ELEVATION}
              sx={{
                marginBottom: 5,
              }}
            >
              <AppBar
                elevation={0}
                position="static"
                sx={{
                  bgcolor: 'quaternary.main',
                  borderTopLeftRadius: theme.shape.borderRadius,
                  borderTopRightRadius: theme.shape.borderRadius,
                  overflow: 'hidden',
                }}
              >
                <Tabs
                  indicatorColor="secondary.contrastText"
                  onChange={(e, value) => setFirstTab(value)}
                  sx={{
                    '& .MuiTab-textColorSecondary': {
                      textTransform: 'unset',
                      color: theme.palette.secondary.contrastText,
                    },
                    '& .MuiTab-textColorSecondary.Mui-selected': {
                      color: theme.palette.secondary.main,
                      backgroundColor: theme.palette.secondary.contrastText,
                      border: 'unset',
                      borderImage: 'unset',
                    },
                  }}
                  textColor="secondary"
                  value={activeFirstTab}
                >
                  <Tab
                    label={t('pages.vehiclefleetapp.slug.tabs.carDetails')}
                    value="carDetails"
                  />
                  <Tab
                    label={t('pages.vehiclefleetapp.slug.tabs.furnishing')}
                    value="furnishing"
                  />
                  {isEmissionsDisplayRequired(item) && (
                    <Tab
                      label={t('pages.vehiclefleetapp.slug.tabs.emissionData')}
                      value="emissionData"
                    />
                  )}
                </Tabs>
              </AppBar>
              <Box
                p={3}
                sx={{
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: theme.palette.secondary.contrastText,
                  borderBottomLeftRadius: theme.shape.borderRadius,
                  borderBottomRightRadius: theme.shape.borderRadius,
                }}
              >
                {activeFirstTab === 'carDetails' && (
                  <CarDetailsTab
                    i18n={i18n}
                    item={item}
                    t={t}
                    theme={theme}
                  />
                )}
                {activeFirstTab === 'furnishing' && (
                  <CarAdDetails item={item} t={t} />
                )}
                {activeFirstTab === 'emissionData' && (
                  <Box>
                    <ConsumptionEmissionsNotice
                      car={item}
                      variant="extended"
                    />
                  </Box>
                )}
              </Box>
            </Paper>
          </Hidden>
          {item.attributes.videoUrl && (
            <Box
              alignItems="center"
              display="flex"
              flexDirection="column"
              my={10}
            >
              <CarPromoVideo url={item.attributes.videoUrl} />
            </Box>
          )}
          <OnPageJumpTarget name="breakpoint-tabs" />
          <Hidden dir="up" size="md">
            <Box
              sx={{
                mb: 2,
                '& .MuiAccordionSummary-root': {
                  bgcolor: 'quaternary.main',
                },
              }}
            >
              <Accordion defaultExpanded>
                <AccordionSummary
                  expandIcon={<FiChevronDown color={theme.palette.primary.main} />}
                  sx={{
                    color: theme.palette.primary.main,
                  }}
                >
                  <Typography>
                    {t('pages.vehiclefleetapp.slug.tabs.requests')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: theme.palette.primary.main,
                    borderBottomLeftRadius: theme.shape.borderRadius,
                    borderBottomRightRadius: theme.shape.borderRadius,
                  }}
                >
                  <CarRequestTab
                    i18n={i18n}
                    item={item}
                    t={t}
                  />
                </AccordionDetails>
              </Accordion>
              <Accordion>
                <AccordionSummary
                  expandIcon={<FiChevronDown color={theme.palette.primary.main} />}
                  sx={{
                    color: theme.palette.primary.main,
                  }}
                >
                  <Typography>
                    {t('pages.vehiclefleetapp.slug.tabs.contact')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <ContactTab
                    contacts={contacts}
                    item={item}
                    onShowRequestForm={handleShowRequestForm}
                    t={t}
                  />
                </AccordionDetails>
              </Accordion>
            </Box>
          </Hidden>
          <Hidden dir="down" size="md">
            <Paper elevation={DEFAULT_ELEVATION}>
              <AppBar
                elevation={0}
                position="static"
                sx={{
                  bgcolor: 'quaternary.main',
                  borderTopLeftRadius: theme.shape.borderRadius,
                  borderTopRightRadius: theme.shape.borderRadius,
                  overflow: 'hidden',
                }}
              >
                <Tabs
                  indicatorColor="secondary.contrastText"
                  onChange={(e, value) => setSecondTab(value)}
                  sx={{
                    '& .MuiTab-textColorSecondary': {
                      textTransform: 'unset',
                      color: theme.palette.secondary.contrastText,
                    },
                    '& .MuiTab-textColorSecondary.Mui-selected': {
                      color: theme.palette.secondary.main,
                      backgroundColor: theme.palette.secondary.contrastText,
                      border: 'unset',
                      borderImage: 'unset',
                    },
                  }}
                  textColor="secondary"
                  value={activeSecondTab}
                >
                  {/* <Tab */}
                  {/*   label="360° Ansicht" */}
                  {/*   value="twinner" */}
                  {/* /> */}
                  {/* {partsShopMapping && ( */}
                  {/*   <Tab */}
                  {/*     label={t('pages.vehiclefleetapp.slug.tabs.accessoriesAndParts')} */}
                  {/*     value="store" */}
                  {/*   /> */}
                  {/* )} */}
                  <Tab
                    label={t('pages.vehiclefleetapp.slug.tabs.requests')}
                    value="request"
                  />
                  <Tab
                    label={t('pages.vehiclefleetapp.slug.tabs.contact')}
                    value="contact"
                  />
                </Tabs>
              </AppBar>
              <Box
                p={3}
                sx={{
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: theme.palette.secondary.contrastText,
                  borderBottomLeftRadius: theme.shape.borderRadius,
                  borderBottomRightRadius: theme.shape.borderRadius,
                }}
              >
                {activeSecondTab === 'twinner' && (
                  <div>
                    <div id="widget-container" />
                  </div>
                )}
                {activeSecondTab === 'request' && (
                  <CarRequestTab
                    i18n={i18n}
                    item={item}
                    t={t}
                  />
                )}
                {activeSecondTab === 'contact' && (
                  <ContactTab
                    contacts={contacts}
                    item={item}
                    onShowRequestForm={handleShowRequestForm}
                    t={t}
                  />
                )}
              </Box>
            </Paper>
          </Hidden>
          <InventoryCarsBlock
            data={{
              title: {
                value: 'Weitere Fahrzeuge',
                displayVariant: 'h4',
              },
              displayVariant: 'slider',
              fallbackIfNoVehiclesFound: true,
              branches: [],
              inventoryCarFilters: [],
              inventoryCarFilterGroups: [{
                filters: [
                  {
                    condition: 'Include',
                    type: 'Brand',
                    value: item.attributes.brand,
                  },
                  {
                    condition: 'Include',
                    type: 'Model',
                    value: item.attributes.baselineModel,
                  },
                  {
                    condition: 'Exclude',
                    type: 'VehicleId',
                    value: item.attributes.vehicleId,
                  },
                ],
              }],
            }}
          />
          {/* partsShopMapping && (
            <Box mt={5}>
              <Typography component="h3" textAlign="center" variant="h3">
                Passendes Zubehör
              </Typography>
              <Box mt={3}>
                <PartsStore
                  dealerId={partsShopMapping.dealerId}
                  display="fullShop"
                  fin={item.attributes.vehicleIdentificationNumber}
                  hsn={item.attributes.hsn}
                  model={item.attributes.baselineModel}
                  tsn={item.attributes.tsn}
                />
              </Box>
            </Box>
          ) */}
          {/* <CarAdFooter */}
          {/*   contacts={contacts} */}
          {/*   item={item} */}
          {/*   t={t} */}
          {/* /> */}
          {/*! item.attributes.isPurchased && (
            <FinancingCalculator images={images} item={item} />
          ) */}
        </Container>
      </DefaultTemplate>
    </>
  );
}

function CarRequestTab(props) {
  const {
    item,
    i18n,
    t,
  } = props;
  return (
    <>
      <Box>
        <Typography variant="h6">
          {t('pages.vehiclefleetapp.slug.testDrive')}
        </Typography>
        <Box mt={3}>
          <Link href="https://www.etermin.net/schimmel" legacyBehavior>
            <Button
              color="primary"
              component="a"
              rel="noopener nofollow"
              startIcon={
                <FiCalendar />
              }
              target="_blank"
              variant="contained"
            >
              {t('pages.vehiclefleetapp.slug.arrangeTestDriveNow')}
            </Button>
          </Link>
        </Box>
      </Box>
      <Box mb={3} mt={5}>
        <Divider />
      </Box>
      <Typography variant="h6">
        {t('pages.vehiclefleetapp.slug.individualRequest')}
      </Typography>
      <ModelRequestForm
        branch={getLocalizedBranch(item, i18n)}
        message={t('pages.vehiclefleetapp.slug.modelRequestFormMessage', {
          brand: item.attributes.brand,
          model: item.attributes.baselineModel,
          vehicleId: item.attributes.vehicleId,
        })}
        vehicle={item}
      />
    </>
  );
}

function CarAdHeader(props) {
  const { t } = props;
  const handleGoBack = () => {
    window.history.back();
  };

  return (
    <Box>
      <Hidden dir="down" size="md">
        <Button
          color="primary"
          onClick={handleGoBack}
          startIcon={<FiChevronLeft />}
          variant="contained"
        >
          {t('pages.vehiclefleetapp.slug.back')}
        </Button>
      </Hidden>
      <Hidden dir="up" size="md">
        <IconButton onClick={handleGoBack}>
          <FiChevronLeft />
        </IconButton>
      </Hidden>
    </Box>
  );
}

function CarAdSummary(props) {
  const {
    t,
    i18n,
    item,
    carCategory,
    detailUrl,
    isPurchasingEnabled,
    contacts,
    contactsLoading,
    // Handler
    onCheckout,
    onShowFinancingForm,
    onShowRequestForm,
    modelPageUrl,
    handleShare,
  } = props;
  const theme = useTheme();

  let deliveryDate = null;

  if (item.attributes.deliveryDate
    && DateTime.fromISO(item.attributes.deliveryDate)
      .startOf('day') > DateTime.now()
      .startOf('day')) {
    deliveryDate = DateTime.fromISO(item.attributes.deliveryDate)
      .toFormat('dd.MM.yyyy');
  }

  let carState = 'usedVehicle';
  const showPrice = !item.attributes.isDummyVehicle || item.attributes.price > 0;

  if (!item.attributes.initialRegistrationDate) {
    carState = 'newVehicle';
  } else if (item.attributes.isDemonstrationVehicle) {
    carState = 'demonstrationCar';
  } else if (item.attributes.isDailyAdmission) {
    carState = 'dailyAdmission';
  }

  return (
    <>
      <Hidden dir="down" size="md">
        <CardAdSummaryHeader
          handleShare={handleShare}
          item={item}
          modelPageUrl={modelPageUrl}
          theme={theme}
        />
      </Hidden>
      <Box mt={3}>
        <CarListItemCardContent
          carState={carState}
          deliveryDate={deliveryDate}
          detailUrl={detailUrl}
          item={item}
          showLeasingReference
          showPrice={showPrice}
          showTransferCosts
          t={t}
          theme={theme}
        />
      </Box>
      <Box mb={3}>
        <Button
          color="primary"
          fullWidth
          onClick={onShowRequestForm}
          sx={{
            borderRadius: 1,
            mb: 1,
          }}
          variant="contained"
        >
          {t('pages.vehiclefleetapp.slug.inquireNow')}
        </Button>
        <Button
          color="default"
          fullWidth
          onClick={(e) => onShowRequestForm(e, true)}
          sx={{
            borderRadius: 1,
          }}
          variant="outlined"
        >
          {t('pages.vehiclefleetapp.slug.arrangeTestDrive')}
        </Button>
      </Box>
      {isEmissionsDisplayRequired(item) && (
        <ConsumptionEmissionsNotice
          car={item}
          variant="extended"
        />
      )}
    </>
  );
}

function CardAdSummaryHeader(props) {
  const {
    item,
    modelPageUrl,
    theme,
    handleShare,
  } = props;

  const title = (
    <Typography
      component="h1"
      sx={{
        fontSize: '24px',
      }}
      variant="h3"
    >
      <strong>
        {transformBrandTitle(item.attributes.brand)}
      </strong>
      {' '}
      {item.attributes.baselineModel}
      {/* <span className="seo-discriminator"> */}
      {/*   {' '} */}
      {/*   {i18n.language} */}
      {/* </span> */}
    </Typography>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 2,
      }}
    >
      <Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Hidden dir="down" size="sm">
            <Image
              alt={`${item.attributes.brand} Logo`}
              isFluid
              src={`/images/car-brands/${BRAND_LOGO_MAP[item.attributes.brand]}`}
              styles={{
                width: 40,
                maxWidth: 40,
                marginRight: theme.spacing(2),
              }}
            />
          </Hidden>
          {modelPageUrl
            ? (
              <Link href={modelPageUrl} legacyBehavior>
                <a>
                  {title}
                </a>
              </Link>
            )
            : title}
        </Box>
        <ModelName
          component="div"
          name={`${item.attributes.model} - ${item.attributes.vehicleId}`}
          sx={{
            fontSize: '13px',
          }}
          variant="caption"
        />
      </Box>
      <ShareButton handleShare={handleShare} />
    </Box>
  );
}

function CarDetailsTab(props) {
  const {
    t,
    i18n,
    item,
    theme,
    isAccordion,
  } = props;

  const facts = getCarFacts(t, i18n)
    .map((fact) => ({
      key: fact.key,
      value: fact.getValue ? fact.getValue(item) : item.attributes[fact.key],
    }))
    .filter((fact) => fact.value);

  return (
    <Box>
      <Grid2 container spacing={2}>
        <Grid2
          size={{
            md: 4,
            xs: 12,
          }}
          sx={{
            mr: !isAccordion ? 3 : undefined,
          }}
        >
          {facts.slice(0, !isAccordion ? 9 : undefined)
            .map((fact) => (
              <Box
                key={fact.key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 0.5,
                }}
              >
                <Typography variant="body2">
                  {`${t(`pages.vehiclefleetapp.slug.QuickFactsList.${fact.key}`)}:`}
                </Typography>
                <Typography variant="body2">
                  {fact.value}
                </Typography>
              </Box>
            ))}
        </Grid2>
        {!isAccordion && facts.length > 9 && (
          <Grid2
            size={{
              md: 4,
              xs: 12,
            }}
            sx={{
              paddingLeft: 4,
              borderLeftWidth: '1px',
              borderLeftColor: theme.palette.primary.main,
              borderLeftStyle: 'solid',
            }}
          >
            {facts.slice(9)
              .map((fact) => (
                <Box
                  key={fact.key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 0.5,
                  }}
                >
                  <Typography variant="body2">
                    {`${t(`pages.vehiclefleetapp.slug.QuickFactsList.${fact.key}`)}:`}
                  </Typography>
                  <Typography variant="body2">
                    {fact.value}
                  </Typography>
                </Box>
              ))}
          </Grid2>
        )}
      </Grid2>
    </Box>
  );
}

function ContactTab(props) {
  const {
    t,
    item,
    contacts,
    contactsLoading,
    onShowRequestForm,
  } = props;
  return (
    <Grid2 container spacing={5}>
      <Grid2
        size={{
          md: 6,
          xs: 12,
        }}
      >
        <Typography component="h3" variant="h6">
          {t('pages.vehiclefleetapp.slug.ourSalesConsultants')}
        </Typography>
        <Box mt={3}>
          {contactsLoading ? (
            <LinearProgress />
          ) : (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <>
              {contacts?.length > 0 ? (
                <StaffMember
                  employee={contacts[0]}
                  inventoryCar={item}
                  onShowRequestForm={onShowRequestForm}
                  variant="horizontal"
                />
              ) : (
                <StaffMember
                  employee={null}
                  inventoryCar={item}
                  onShowRequestForm={onShowRequestForm}
                  variant="horizontal"
                />
              )}
            </>
          )}
        </Box>
      </Grid2>
      <Grid2
        size={{
          md: 6,
          xs: 12,
        }}
      >
        <CarAdFooter
          contacts={contacts}
          item={item}
          t={t}
        />
      </Grid2>
    </Grid2>
  );
}

function CarAdDetails(props) {
  const {
    t,
    item,
  } = props;
  const technicalFacts = [];

  if (item.attributes.ccm) {
    technicalFacts.push({
      key: 'ccm',
      // eslint-disable-next-line react/jsx-one-expression-per-line
      value: <span>{item.attributes.ccm} cm<sup>3</sup></span>,
    });
  }

  if (item.attributes.numOfSeats) {
    technicalFacts.push({
      key: 'numOfSeats',
      value: item.attributes.numOfSeats,
    });
  }

  if (item.attributes.doors) {
    technicalFacts.push({
      key: 'doors',
      value: item.attributes.doors,
    });
  }

  if (item.attributes.pollutantClassDetail) {
    technicalFacts.push({
      key: 'pollutantClassDetail',
      value: item.attributes.pollutantClassDetail,
    });
  }

  if (item.attributes.pollutionBadge) {
    technicalFacts.push({
      plain: true,
      key: 'pollutionBadge',
      value: (
        <PollutionBadge badge={item.attributes.pollutionBadge} size="medium" />
      ),
    });
  }

  if (item.attributes.previousOwner) {
    technicalFacts.push({
      key: 'previousOwner',
      value: item.attributes.previousOwner,
    });
  }

  if (item.attributes.emptyWeight) {
    technicalFacts.push({
      key: 'emptyWeight',
      value: `${item.attributes.emptyWeight} kg`,
    });
  }

  if (item.year_of_manufacture) {
    technicalFacts.push({
      key: 'year_of_manufacture',
      value: item.attributes.yearOfManufacture,
    });
  }

  if (item.attributes.baseColorOutside) {
    technicalFacts.push({
      key: 'baseColorOutside',
      value: `${item.attributes.baseColorOutside} (${item.attributes.color})`,
    });
  }

  if (item.attributes.interiorType) {
    technicalFacts.push({
      key: 'interiorType',
      value: INTERIOR_TYPE[item.attributes.interiorType],
    });
  }

  if (item.attributes.interiorColor) {
    technicalFacts.push({
      key: 'interiorColor',
      value: INTERIOR_COLOR[item.attributes.interiorColor],
    });
  }

  if (item.attributes.deliveryDate
    && DateTime.fromISO(item.attributes.deliveryDate)
      .startOf('day') > DateTime.now()
      .startOf('day')) {
    technicalFacts.push({
      key: 'deliveryDate',
      value: DateTime.fromISO(item.attributes.deliveryDate)
        .toFormat('dd.MM.yyyy'),
    });
  }

  if (item.attributes.deliveryDeadline) {
    technicalFacts.push({
      key: 'deliveryDeadline',
      value: `${item.attributes.deliveryDeadline} Tage(n)`,
    });
  }

  if (!item.attributes.deliveryDate && !item.attributes.deliveryDeadline) {
    technicalFacts.push({
      key: 'availableFrom',
      value: 'sofort',
    });
  }

  return (
    <Box>
      <Grid2 container spacing={3}>
        <Grid2 size={{
          md: 6,
          xs: 12,
        }}
        >
          {/* <Box> */}
          {/*   <Typography variant="h4"> */}
          {/*     {t('pages.vehiclefleetapp.slug.furtherTechnicalData')} */}
          {/*   </Typography> */}
          {/*   <Box mt={3}> */}
          {/*     <QuickFactsList items={technicalFacts} /> */}
          {/*   </Box> */}
          {/* </Box> */}
          <Box>
            <Typography variant="h6">
              {t('pages.vehiclefleetapp.slug.furnishing')}
            </Typography>
            {Object.keys(VEHICLE_EQUIPMENT)
              .map((key) => {
                const section = VEHICLE_EQUIPMENT[key];
                const { title } = section;
                const items = [];
                const keys = Object.keys(section)
                  .filter((k) => k !== 'title');

                // eslint-disable-next-line no-restricted-syntax
                for (const sectionKey of keys) {
                  if (item.attributes.carProperties[sectionKey] === '1') {
                    items.push(section[sectionKey]);
                  }
                }

                if (items.length === 0) {
                  return null;
                }

                return (
                  <Box key={key} mt={2}>
                    <Typography variant="subtitle2">
                      {title}
                    </Typography>
                    <ul>
                      {items.map((equipment) => (
                        <li key={equipment}>
                          <Typography variant="body2">
                            {equipment}
                          </Typography>
                        </li>
                      ))}
                    </ul>
                  </Box>
                );
              })}
          </Box>
        </Grid2>
        <Grid2 size={{
          md: 6,
          xs: 12,
        }}
        >
          <Box>
            <Typography variant="h6">
              {t('pages.vehiclefleetapp.slug.vehicleDescription')}
            </Typography>
            <Box mt={3}>
              <Typography
                component="div"
                dangerouslySetInnerHTML={{
                  __html: item.attributes.description ?? '',
                }}
                variant="body2"
              />
            </Box>
          </Box>
        </Grid2>
      </Grid2>
    </Box>
  );
}

function CarAdFooter(props) {
  const {
    t,
    item,
    contacts,
  } = props;

  return (
    <>
      <Box>
        <Typography variant="h6">
          {t('pages.vehiclefleetapp.slug.contact')}
        </Typography>
        <Box mt={2}>
          <Typography component="div" variant="caption">
            {get(contacts, '[0].primary_branch?.name') && (
              <>
                {t('pages.vehiclefleetapp.slug.location')}
                {' '}
                <MuiLink href={get(contacts, '[0].primary_branch.pageUrl')}>{get(contacts, '[0].primary_branch.name')}</MuiLink>
                <br />
              </>
            )}
            {get(contacts, '[0].name', 'CSB Schimmel Automobile GmbH')}
            <br />
            {get(contacts, '[0].position', 'Hyundai- & Mitsubishi-Vertragshändler')}
          </Typography>
          <Box mt={2}>
            <Button
              component="a"
              href={`tel:${get(contacts, '[0].phone', item.attributes.branch.data.attributes.salesPhone)}`}
              startIcon={<FiPhone />}
              sx={{
                '& .MuiButtonBase-root': {
                  whiteSpace: 'normal',
                  wordBreak: 'break-all',
                },
              }}
            >
              {t('pages.vehiclefleetapp.slug.phone')}
              <Typography
                sx={{
                  fontSize: 'inherit',
                  fontWeight: 'inherit',
                  ml: 1,
                  whiteSpace: 'normal',
                  wordBreak: 'break-all',
                }}
              >
                {get(contacts, '[0].phone', item.attributes.branch.data.attributes.salesPhone)}
              </Typography>
            </Button>
            <Button
              component="a"
              href={`mailto:${get(contacts, '[0].email', item.attributes.branch.data.attributes.primaryEmail)}`}
              startIcon={<FiMail />}
              sx={{
                '& .MuiButtonBase-root': {},
              }}
            >
              {t('pages.vehiclefleetapp.slug.email')}
              <Typography
                sx={{
                  fontSize: 'inherit',
                  fontWeight: 'inherit',
                  ml: 1,
                  whiteSpace: 'normal',
                  wordBreak: 'break-all',
                }}
              >
                {get(contacts, '[0].email', item.attributes.branch.data.attributes.primaryEmail)}
              </Typography>
            </Button>
          </Box>
          <Box mt={2}>
            <Typography>
              {item.attributes.branch.data.attributes.addressStreet}
              <br />
              {item.attributes.branch.data.attributes.addressZipcode}
              {' '}
              {item.attributes.branch.data.attributes.addressCity}
            </Typography>
          </Box>
        </Box>
      </Box>
      {/* <Box mt={5}> */}
      {/*   <Divider /> */}
      {/* </Box> */}
      {/* <Box mt={5}>
        <Typography variant="body2">
          {t('pages.vehiclefleetapp.slug.moreInformation')}
          <Button
            component="a"
            href="https://www.dat.de/co2/"
            rel="noreferrer"
            size="small"
            target="_blank"
          >
            www.dat.de/co2
          </Button>
          {t('pages.vehiclefleetapp.slug.freeOfCharge')}
        </Typography>
      </Box> */}
    </>
  );
}

function CarPromoVideo({ url }) {
  return (
    <>
      <Container sx={{ maxWidth: '800px !important' }}>
        <Paper elevation={18} square>
          <Box
            sx={{
              // Responsive embed
              position: 'relative',
              paddingBottom: '56.25%',
              paddingTop: 0,
              height: 0,
            }}
          >
            <EmbedElementDynamic
              className="player"
              data={{
                provider: 'youtube',
                url,
              }}
              options={{
                // https://developers.google.com/youtube/player_parameters
                modestbranding: 1,
                rel: 0,
              }}
            />
          </Box>
        </Paper>
      </Container>
      <style global jsx>
        {`
          .player, .player object, .player embed {
            display: block;
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
          }
        `}
      </style>
    </>
  );
}

function CarPartsShop(props) {
  const {
    item,
    config,
  } = props;
  const { i18n } = useTranslation();
  const hasConsent = useConsent('partsStore');

  if (!item.attributes.vehicleIdentificationNumber) {
    return null;
  }

  if (!hasConsent) {
    return <ConsentRequiredFallback />;
  }

  const data = {
    'data-model': item.attributes.model,
    'data-language': i18n.language,
    'data-dealerid': config.dealerId,
    'data-fin': item.attributes.vehicleIdentificationNumber,
  };

  return (
    <Box>
      <Typography
        textAlign="center"
        variant="h4"
      >
        Passendes Zubehör
      </Typography>
      <link href="https://selekt.sl-gcp.itt-dev.de/static/css/main.css" rel="stylesheet" />
      <div
        {...data}
        className="vehicle-cart"
        id="itt-sl-ucl"
      />
      <Script src="https://selekt.sl-gcp.itt-dev.de/static/js/runtime-main.js" />
      <Script src="https://selekt.sl-gcp.itt-dev.de/static/js/main.js" />
    </Box>
  );
}

const DEFAULT_ELEVATION = 3;

const CONTACT_RAND_BRANCH_MAPPING = {
  HYUNDAI: {
    // Berlin-Hohenschönhausen
    1: [3, 605],
    // Berlin-Schöneweide
    2: [54],
    // Berlin-Spandau
    3: [25, 519],
    // Bernau
    4: [26, 56],
  },
  MITSUBISHI: {
    // Berlin-Hohenschönhausen
    1: [5, 33],
    // Berlin-Schöneweide
    2: [54],
    // Berlin-Spandau
    3: [519],
    // Bernau
    4: [26, 56],
  },
  MG: {
    // Berlin-Hohenschönhausen
    1: [5, 33],
    // Berlin-Schöneweide
    2: [],
    // Berlin-Spandau
    3: [519],
    // Bernau
    4: [26, 56],
  },
  FUSO: {
    // Berlin-Hohenschönhausen
    1: [595, 630],
    // Berlin-Schöneweide
    2: [],
    // Berlin-Spandau
    3: [],
    // Bernau
    4: [],
  },
  MAXUS: {
    // Berlin-Hohenschönhausen
    1: [595, 630],
    // Berlin-Schöneweide
    2: [],
    // Berlin-Spandau
    3: [],
    // Bernau
    4: [],
  },
  CENNTRO: {
    // Berlin-Hohenschönhausen
    1: [595],
    // Berlin-Schöneweide
    2: [],
    // Berlin-Spandau
    3: [],
    // Bernau
    4: [],
  },
};

const PHONE_BRANCH_OVERRIDE = {
  // Berlin-Hohenschönhausen
  1: '+4930983002650',
  // Berlin-Schöneweide
  2: '+4930983002670',
  // Berlin-Spandau
  3: '+4930983002660',
  // Bernau
  4: '+4930983002680',
};

const NOT_AVAILABLE_URL = {
  de: '/fahrzeugsuche/nicht-verfuegbar',
  en: '/en/car-search/not-available',
  es: '/es/buscar-coche/no-disponible',
  ru: '/ru/poisk-avtomobilya/nedostupno',
  tr: '/tr/araba-arama/mevcut-degil',
};

export async function getServerSideProps(ctx) {
  const {
    query,
    res,
    locale,
  } = ctx;
  const [graphQlClient, appData] = await getAppData(ctx);

  const {
    data,
    error,
  } = await graphQlClient.query({
    query: FETCH_ITEM,
    variables: {
      slug: query.slug,
    },
  });

  if (error) {
    throw error;
  }

  const item = data?.inventoryCars.data[0];

  if (!item) {
    return {
      redirect: {
        destination: NOT_AVAILABLE_URL[locale],
        permanent: true,
      },
    };
  }

  if (item.attributes.isDeleted) {
    return {
      redirect: {
        destination: `${NOT_AVAILABLE_URL[locale]}#id=${item.id}&brand=${item.attributes.brand}&model=${item.attributes.baselineModel}`,
        permanent: true,
      },
    };
  }

  if (res && data.cacheState.isEnabled) {
    // 10 min
    res.setHeader('Cache-Control', 'public, max-age=600');
  }

  return {
    props: {
      ...appData,
      item: item ?? null,
      contacts: await getContacts(graphQlClient, item),
      carSearchConfig: data.carSearchConfig.data,
      isGlobalNoticeHidden: true,
    },
  };
}

async function getContacts(graphQlClient, item) {
  if (!item) {
    return [];
  }

  let overridePhone = false;
  let contactIds = [];

  if (item.attributes.isDummyVehicle && item.attributes.isCarSubscription) {
    // Overwrite the id for dummy cars
    contactIds = [524];
  } else if (!item.isNewCar && !CONTACT_RAND_BRANCH_MAPPING[item.attributes.brand]) {
    // #! Gebrauchtfahrzeuge:
    // Wie gestern besprochen, sollen für alle Gebrauchtfahrzeuge von Fremdmarken
    // die Verkäufer des jeweiligen Standorts hinterlegt werden, die dann rotieren.
    contactIds = uniq(Object
      .entries(CONTACT_RAND_BRANCH_MAPPING)
      .flatMap(([brand, branches]) => branches[item.attributes.branch?.data?.id] ?? []));
  } else {
    contactIds = CONTACT_RAND_BRANCH_MAPPING[item.attributes.brand]?.[item.attributes.branch?.data?.id] ?? [];

    if (contactIds.length > 0) {
      overridePhone = true;
    }
  }

  // Fallback to default onine contact
  if (!contactIds.length) {
    contactIds = [1];
  }

  const contactId = contactIds[Math.floor(Math.random() * contactIds.length)];
  const {
    data: data2,
    error: error2,
  } = await graphQlClient.query({
    query: FETCH_CONTACT,
    variables: {
      filters: {
        id: { eq: contactId },
      },
    },
  });

  if (error2) {
    throw error2;
  }

  let contacts = data2?.employees.data ?? [];

  if (overridePhone) {
    contacts = contacts.map((contact) => ({
      ...contact,
      attributes: {
        ...contact.attributes,
        phone: PHONE_BRANCH_OVERRIDE[item.attributes.branch?.data?.id] || contact.attributes.phone,
      },
    }));
  }

  return contacts;
}

function getLocalizedBranch(item, i18n) {
  if (i18n.language === 'de') {
    return item.attributes.branch.data;
  }

  return item.attributes.branch.data.attributes.localizations.data.find((b) => b.attributes.locale === i18n.language);
}

const FETCH_ITEM = gql`
  query GetInventoryCar($slug: String) {
    inventoryCars(filters: { slug: { eq: $slug } }) {
      data {
        id
        attributes {
          isDeleted
          isPurchased
          internalId
          vehicleId
          branch {
            data {
              id
              attributes {
                locale
                branchId
                name
                salesPhone
                primaryEmail
                addressStreet
                addressZipcode
                addressCity
                pageUrl
                localizations {
                  data {
                    id
                    attributes {
                      locale
                      branchId
                      name
                      salesPhone
                      primaryEmail
                      addressStreet
                      addressZipcode
                      addressCity
                      pageUrl
                    }
                  }
                }
              }
            }
          }
          slug
          category
          brand
          model
          baselineModel
          description
          power
          horsePower
          isNewCar
          isExportVehicle
          generalInspectionDate
          initialRegistrationDate
          isDemonstrationVehicle
          isDailyAdmission
          mileage
          price
          previousPrice
          firstSalesPrice
          originalPrice
          transferCosts
          currency
          isVatAvailable
          isOldtimer
          isSale
          isDummyVehicle
          hsn
          tsn
          vehicleIdentificationNumber
          color
          baseColorOutside
          fuelConsumptionCombined
          fuelConsumptionUrban
          fuelConsumptionOutoftown
          emissions
          fuelType
          transmissionType
          energyEfficiencyClass
          airConditioner
          carAttributes
          ccm
          numOfSeats
          doors
          pollutantClass
          pollutantClassDetail
          pollutionBadge
          deliveryDate
          deliveryDeadline
          previousOwner
          emptyWeight
          yearOfManufacture
          interiorColor
          interiorType
          isImmediatelyAvailable
          leasingRate
          financingRate
          carProperties
          isCarSubscription
          leasingProduct
          videoUrl
          ${GRAPHQL_REQUIRED_PROPS_FOR_CONSUMPTION}
        }
      }
    }
    carSearchConfig {
      data {
        id
        attributes {
          isPurchasingEnabled
          urlMappings {
            model
            url
          }
        }
      }
    }
    cacheState {
      isEnabled
    }
  }
`;

const FETCH_CONTACT = gql`
  query FetchContact($filters: EmployeeFiltersInput) {
    employees(filters: $filters) {
      data {
        id
        attributes {
          name
          position
          phone
          phoneWhatsapp
          email
          image {
            ${MediaElement.graphQlSchema}
          }
          primary_branch {
            data {
              attributes {
                name
                pageUrl
              }
            }
          }
          languages(sort: "name") {
            data {
              id
              attributes {
                name
                emoji
              }
            }
          }
          image {
            ${MediaElement.graphQlSchema}
          }
        }
      }
    }
  }
`;

function getCarFacts(t, i18n) {
  const facts = [
    {
      key: 'brand',
      getValue: (car) => transformBrandTitle(car.attributes.brand),
    },
    {
      key: 'baselineModel',
    },
    {
      key: 'vehicleType',
      getValue: (car) => {
        let carState = 'usedVehicle';
        if (!car.attributes.initialRegistrationDate) {
          carState = 'newVehicle';
        } else if (car.attributes.isDemonstrationVehicle) {
          carState = 'demonstrationCar';
        } else if (car.attributes.isDailyAdmission) {
          carState = 'dailyAdmission';
        }
        return t(`pages.vehiclefleetapp.slug.vehicleTypes.${carState}`);
      },
    },
    {
      key: 'vehicleId',
    },
    {
      key: 'location',
      getValue: (car) => getLocalizedBranch(car, i18n).attributes.name,
    },
    {
      key: 'power',
      getValue: (car) => `${car.attributes.power} kW (${car.attributes.horsePower} PS)`,
    },
    {
      key: 'ccm',
      getValue: (car) => (
        <span>
          {car.attributes.ccm}
          {' '}
          cm
          <sup>3</sup>
        </span>
      ),
    },
    {
      key: 'mileage',
      getValue: (car) => (car.attributes.mileage ? `${car.attributes.mileage} km` : null),
    },
    {
      key: 'initialRegistrationDate',
      getValue: (car) => (
        car.attributes.initialRegistrationDate
        && DateTime.fromISO(car.attributes.initialRegistrationDate).isValid
          ? DateTime
            .fromISO(car.attributes.initialRegistrationDate)
            .toFormat('MM-yyyy')
          : null
      ),
    },
    {
      key: 'fuelType',
      getValue: (car) => {
        let fuelType = i18n.exists(`components.vehiclefleet.InventoryCarListItem.fuelTypes.${car.attributes.isPluginHybrid ? 'plugInHybrid' : car.attributes.fuelType}`)
          ? t(`components.vehiclefleet.InventoryCarListItem.fuelTypes.${car.attributes.isPluginHybrid ? 'plugInHybrid' : car.attributes.fuelType}`)
          : car.attributes.fuelType;

        if (car.attributes.fuelType === 'hybrid') {
          fuelType = `${fuelType} ${t('components.vehiclefleet.InventoryCarListItem.hybridFuelType.hybrid')}`;
        }

        if (car.attributes.fuelType === 'hybridDiesel') {
          fuelType = `${fuelType} ${t('components.vehiclefleet.InventoryCarListItem.hybridFuelType.hybridDiesel')}`;
        }

        if (car.attributes.fuelType === 'plugInHybrid') {
          fuelType = `${fuelType} ${t('components.vehiclefleet.InventoryCarListItem.hybridFuelType.plugInHybrid')}`;
        }

        return fuelType;
      },
    },
    {
      key: 'transmissionType',
      getValue: (car) => {
        const transmissionType = i18n.exists(`components.vehiclefleet.InventoryCarListItem.transmissionTypes.${car.attributes.transmissionType}`)
          ? t(`components.vehiclefleet.InventoryCarListItem.transmissionTypes.${car.attributes.transmissionType}`)
          : car.attributes.transmissionType;

        return transmissionType;
      },
    },
    {
      key: 'doors',
    },
    {
      key: 'emptyWeight',
      getValue: (car) => `${car.attributes.emptyWeight} kg`,
    },
    {
      key: 'baseColorOutside',
      getValue: (car) => {
        const baseColor = i18n.exists(`components.vehiclefleet.InventoryCarListItem.colors.${car.attributes.baseColorOutside}`)
          ? t(`components.vehiclefleet.InventoryCarListItem.colors.${car.attributes.baseColorOutside}`)
          : car.attributes.baseColorOutside;
        const color = i18n.exists(`components.vehiclefleet.InventoryCarListItem.colors.${car.attributes.color}`)
          ? t(`components.vehiclefleet.InventoryCarListItem.colors.${car.attributes.color}`)
          : car.attributes.color;
        return `${baseColor || ''}  ${color ? `(${color})` : ''}`;
      },
    },
    {
      key: 'interiorColor',
      getValue: (car) => {
        const interiorColor = i18n.exists(`components.vehiclefleet.SearchAndFilterBar.FeaturesFilterGroup.colors.${car.attributes.interiorColor}`)
          ? t(`components.vehiclefleet.SearchAndFilterBar.FeaturesFilterGroup.colors.${car.attributes.interiorColor}`)
          : car.attributes.interiorColor;

        return interiorColor;
      },
    },
    {
      key: 'interiorType',
      getValue: (car) => {
        const interiorType = i18n.exists(`components.vehiclefleet.InventoryCarListItem.interiorTypes.${car.attributes.interiorType}`)
          ? t(`components.vehiclefleet.InventoryCarListItem.interiorTypes.${car.attributes.interiorType}`)
          : car.attributes.interiorType;

        return interiorType;
      },
    },
    {
      key: 'previousOwner',
    },
  ];

  return facts;
}
